<template>
  <v-dialog v-model="value" fullscreen hide-overlay persistent>
    <v-card>
      <v-toolbar dark color="primary">
        <v-btn icon dark @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>
          {{ $t("report.add.title") }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn dark text @click="$emit('input', false)">
            {{ $t("button.close") }}
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text>
        <v-app id="inspire">
          <v-form ref="form" v-model="valid">
            <v-container fluid>
              <v-row>
                <v-col cols="3">
                  <v-text-field
                    v-model.number="amount"
                    :label="$t('report.add.amount')"
                    required
                    type="number"
                    readonly
                    hide-details
                    outlined
                    filled
                  ></v-text-field>
                </v-col>
                <v-col cols="3">
                  <v-text-field
                    v-model.number="price"
                    :label="$t('report.add.totalPrice')"
                    required
                    prefix="T$"
                    type="number"
                    readonly
                    hide-details
                    outlined
                    filled
                  ></v-text-field>
                </v-col>
                <v-col cols="3">
                  <v-text-field
                    v-model.number="wallet"
                    :label="$t('report.add.wallet')"
                    required
                    prefix="T$"
                    type="number"
                    readonly
                    hide-details
                    outlined
                    filled
                  ></v-text-field>
                </v-col>
                <v-col cols="3">
                  <v-text-field
                    v-model.number="final_balance"
                    :label="$t('report.add.finalBalance')"
                    required
                    prefix="T$"
                    type="number"
                    readonly
                    hide-details
                    outlined
                    filled
                  ></v-text-field>
                </v-col>
              </v-row>
              <div class="page">
                <h5 class="text-h5">
                  {{ $t("report.add.listTitle") }}
                </h5>
                <v-data-table
                  v-model="selected"
                  :headers="headers"
                  :items="items"
                  :items-per-page="5"
                  item-key="filename"
                  @click:row="toggle"
                  :footer-props="footerProps"
                >
                  <template v-slot:item.organ="props">
                    <v-chip :color="organTypes[props.item.organ].color" dark>
                      <v-edit-dialog
                        :ref="'edit_organ_' + props.item.filename"
                        :return-value.sync="props.item.organ"
                      >
                        {{ organTypes[props.item.organ].text }}
                        <template v-slot:input>
                          <v-select
                            v-model="props.item.organ"
                            :items="organTypes"
                            item-text="text"
                            item-value="id"
                            single-line
                            autofocus
                            disabled
                            @change="
                              close(
                                'edit_organ_' + props.item.filename,
                                props.item.organ
                              )
                            "
                          >
                          </v-select>
                        </template>
                      </v-edit-dialog>
                    </v-chip>
                  </template>
                  <template v-slot:item.service="props">
                    <v-chip
                      :color="serviceTypes[props.item.service].color"
                      dark
                    >
                      <v-edit-dialog
                        :ref="'edit_service_' + props.item.filename"
                        :return-value.sync="props.item.service"
                      >
                        {{ serviceTypes[props.item.service].text }}
                        <template v-slot:input>
                          <v-select
                            v-model="props.item.service"
                            :items="serviceTypes"
                            item-text="text"
                            item-value="id"
                            single-line
                            autofocus
                            disabled
                            @change="
                              close(
                                'edit_service_' + props.item.filename,
                                props.item.service
                              )
                            "
                          >
                          </v-select>
                        </template>
                      </v-edit-dialog>
                    </v-chip>
                  </template>
                  <template v-slot:item.priority="props">
                    <v-chip color="green" dark>
                      <v-edit-dialog
                        :ref="'edit_priority_' + props.item.filename"
                        :return-value.sync="props.item.priority"
                      >
                        {{ props.item.priority }}
                        <template v-slot:input>
                          <v-select
                            v-model="props.item.priority"
                            :items="selected"
                            item-text="priority"
                            item-value="priority"
                            single-line
                            autofocus
                            @change="
                              closePriority(
                                'edit_priority_' + props.item.filename,
                                props.item.priority
                              )
                            "
                          >
                          </v-select>
                        </template>
                      </v-edit-dialog>
                    </v-chip>
                  </template>
                  <template v-slot:item.comment="props">
                    <v-edit-dialog
                      :ref="'edit_comment_' + props.item.filename"
                      :return-value.sync="props.item.comment"
                      large
                      @open="originalComment = props.item.comment"
                      @save="
                        saveComment(
                          'edit_comment_' + props.item.filename,
                          props.item.comment
                        )
                      "
                    >
                      <div v-if="props.item.comment !== ''">
                        {{ props.item.comment }}
                      </div>
                      <div else> 
                        {{ $t("report.add.commentPlaceholder") }}
                      </div>
                      <template v-slot:input>
                        <div class="mt-4 text-h6">
                          {{ $t("report.add.commentUpdate") }}
                        </div>
                        <v-textarea
                          id="comment-textarea"
                          v-model="props.item.comment"
                          :rules="commentRules"
                          :label="$t('report.add.comment')"
                          single-line
                          auto-grow
                          counter
                          autofocus
                          rows="1"
                        ></v-textarea>
                      </template>
                    </v-edit-dialog>
                  </template>
                </v-data-table>
              </div>
              <v-divider></v-divider>
              <v-row>
                <v-col class="col-md-4">
                  <h5 class="text-h5 mt-4">
                    {{ $t("report.add.priorityListTitle") }}
                  </h5>
                  <div class="page">
                    <v-data-table
                      class="elevation-1"
                      :headers="priorityHeaders"
                      :items="selected"
                      item-key="filename"
                      :no-data-text="$t('report.add.priorityListEmpty')"
                      hide-default-header
                      hide-default-footer
                      disable-sort
                      disable-pagination
                      disable-filtering
                      dense
                      v-sortable-data-table
                      @sorted="saveOrder"
                      :options="priorityOptions"
                    >
                    </v-data-table>
                  </div>
                </v-col>
                <v-col class="col-md-8">
                  <div class="page">
                    <v-btn
                      class="float-right"
                      color="success"
                      :disabled="!selected.length"
                      @click="submit"
                    >
                      {{ $t("report.add.runClassification") }}
                    </v-btn>
                    <v-btn
                      class="warning mr-4 float-right"
                      @click="clear"
                      :disabled="selected.length < 1"
                    >
                      {{ $t("report.add.clearSelection") }}
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-app>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
<script>
import Sortable from "sortablejs";
import { CREATE_REPORTBATCH } from "@/store/modules/service";
import { mapState } from "vuex";

export default {
  props: {
    value: Boolean
  },
  data() {
    return {
      valid: true,
      showList: false,
      // 40x, 10x, 10x
      service: null,
      reportBatch: null,
      serviceDescription: ["1", "2", "3"],
      // rules do not work on v-edit-dialog, validation is done on @save event
      originalComment: "",
      commentMaxLength: 250,
      commentRules: [
        v =>
          (v || "").length <= this.commentMaxLength ||
          (v && "Max characters exceeded")
      ],
      selected: [],
      priorityOptions: {
        sortBy: "priority",
        sortDesc: false
      },
      priorityHeaders: [
        {
          text: "File name",
          value: "filename"
        },
        {
          text: "Priority",
          value: "priority"
        }
      ],
      items: [
        {
          filename: "438-UCH-11-ID - 2020-04-30 14.09.46",
          description: "Benchmark",
          staining: "Benchmark",
          organ: 0,
          prediagnostic: 0,
          service: 0,
          priority: 0,
          output: 0,
          comment: null
        },
        {
          filename: "586-UCH-1-ID - 2021-09-14 09.14.51",
          description: "250856  Tumor neuroendocrino",
          staining: "Hematoxilina-eosina",
          organ: 0,
          prediagnostic: 0,
          service: 0,
          priority: 0,
          output: 0,
          comment: null
        },
        {
          filename: "586-UCH-2-ID - 2021-09-09 10.44.34",
          description: "262954  Colon normal",
          staining: "Hematoxilina-eosina",
          organ: 0,
          prediagnostic: 0,
          service: 0,
          priority: 0,
          output: 0,
          comment: null
        },
        {
          filename: "586-UCH-3-ID - 2021-09-14 10.14.03",
          description: "251251  Colon normal",
          staining: "Hematoxilina-eosina",
          organ: 0,
          prediagnostic: 0,
          service: 0,
          priority: 0,
          output: 0,
          comment: null
        },
        {
          filename: "586-UCH-4-ID - 2021-09-14 09.48.52",
          description: "263177  Adenoma tubular con displasia de bajo grado",
          staining: "Hematoxilina-eosina",
          organ: 0,
          prediagnostic: 0,
          service: 0,
          priority: 0,
          output: 0,
          comment: null
        },
        {
          filename: "586-UCH-5-ID - 2021-09-14 14.55.07",
          description: "253934  Adenoma tubular con displasia de bajo grado",
          staining: "Hematoxilina-eosina",
          organ: 0,
          prediagnostic: 0,
          service: 0,
          priority: 0,
          output: 0,
          comment: null
        },
        {
          filename: "586-UCH-6-ID - 2021-09-09 11.29.27",
          description: "249608-T  Carcinoma indiferenciado",
          staining: "Hematoxilina-eosina",
          organ: 0,
          prediagnostic: 0,
          service: 0,
          priority: 0,
          output: 0,
          comment: null
        },
        {
          filename: "586-UCH-7-ID - 2021-09-09 11.38.13",
          description: "248701  Adenocarcinoma",
          staining: "Hematoxilina-eosina",
          organ: 0,
          prediagnostic: 0,
          service: 0,
          priority: 0,
          output: 0,
          comment: null
        },
        {
          filename: "586-UCH-8-ID - 2021-09-09 11.46.51",
          description: "247275-C Adenocarcinoma bien diferenciado de colon",
          staining: "Hematoxilina-eosina",
          organ: 0,
          prediagnostic: 0,
          service: 0,
          priority: 0,
          output: 0,
          comment: null
        }
        // {
        //   filename: "438-UCH-1-ID - 2020-04-30 15.00.00",
        //   description:
        //     "226220 - CCR - CARCINOMA NEUROENDOCRINO CELULAS GRANDES ALTO GRADO.",
        //   staining: "Hematoxilina-eosina"
        // },
        // {
        //   filename: "438-UCH-10-ID - 2020-04-30 13.55.14",
        //   description:
        //     "248469 - CCR - ADENOCARCINOMA TUBULAR BIEN-MODERADAMENTE DIFERENCIADO",
        //   staining: "Hematoxilina-eosina"
        // },
        // {
        //   filename: "438-UCH-11-ID - 2020-04-30 14.09.46",
        //   description:
        //     "245575 - CCR - ADENOCARCINOMA TUBULAR BIEN-MODERADAMENTE DIFERENCIADO",
        //   staining: "Hematoxilina-eosina"
        // },
        // {
        //   filename: "438-UCH-12-ID - 2020-04-30 12.01.27",
        //   description:
        //     "245730 - CCR - ADENOCARCINOMA TUBULAR BIEN-MODERADAMENTE DIFERENCIADO",
        //   staining: "Hematoxilina-eosina"
        // },
        // {
        //   filename: "582-UCH-1-ID - 2021-07-05 12.18.22",
        //   description: "253360-C - Colon carcinoma indiferenciado",
        //   staining: "Hematoxilina-eosina"
        // },
        // {
        //   filename: "438-UCH-13-ID - 2020-04-30 12.06.52",
        //   description:
        //     "245890 - CCR - ADENOCARCINOMA TUBULAR BIEN-MODERADAMENTE DIFERENCIADO",
        //   staining: "Hematoxilina-eosina"
        // },
        // {
        //   filename: "438-UCH-14-ID - 2020-05-11 08.36.49",
        //   description:
        //     "247073 - CCR - ADENOCARCINOMA TUBULAR BIEN-MODERADAMENTE DIFERENCIADO",
        //   staining: "Hematoxilina-eosina"
        // },
        // {
        //   filename: "438-UCH-15-ID - 2020-04-30 13.11.29",
        //   description:
        //     "239288 - CCR - ADENOCARCINOMA TUBULAR BIEN-MODERADAMENTE DIFERENCIADO",
        //   staining: "Hematoxilina-eosina"
        // },
        // {
        //   filename: "438-UCH-2-ID - 2020-04-29 15.19.48",
        //   description: "241953 - CCR - ADENOCARCINOMA POCO DIFERENCIADO",
        //   staining: "Hematoxilina-eosina"
        // },
        // {
        //   filename: "438-UCH-3-ID - 2020-04-30 13.47.20",
        //   description: "241941 - CCR - ADENOCARCINOMA POCO DIFERENCIADO",
        //   staining: "Hematoxilina-eosina"
        // },
        // {
        //   filename: "438-UCH-4-ID - 2020-04-30 09.15.05",
        //   description: "239476 - CCR - ADENOCARCINOMA POCO DIFERENCIADO",
        //   staining: "Hematoxilina-eosina"
        // },
        // {
        //   filename: "438-UCH-5-ID - 2020-04-30 09.23.30",
        //   description: "229330 - CCR - ADENOCARCINOMA MUCINOSO",
        //   staining: "Hematoxilina-eosina"
        // },
        // {
        //   filename: "438-UCH-6-ID - 2020-04-30 09.32.24",
        //   description: "234675 - CCR - ADENOCARCINOMA MUCINOSO",
        //   staining: "Hematoxilina-eosina"
        // },
        // {
        //   filename: "438-UCH-7-ID - 2020-04-30 15.08.09",
        //   description: "248303 - CCR - ADENOCARCINOMA MUCINOSO",
        //   staining: "Hematoxilina-eosina"
        // },
        // {
        //   filename: "438-UCH-8-ID - 2020-04-30 10.41.08",
        //   description:
        //     "248015 - CCR - ADENOCARCINOMA TUBULAR MODERADO Y MUCINOSO",
        //   staining: "Hematoxilina-eosina"
        // },
        // {
        //   filename: "438-UCH-9-ID - 2020-04-30 10.50.21",
        //   description:
        //     "247363 - CCR - ADENOCARCINOMA TUBULAR MODERADO Y MUCINOSO",
        //   staining: "Hematoxilina-eosina"
        // },
        // {
        //   filename: "439-UCH-1-ID - 2020-04-30 13.20.38",
        //   description:
        //     "199337 - ADENOCARCINOMA TUBULAR BIEN-MODERADAMENTE DIFERENCIADO",
        //   staining: "Hematoxilina-eosina"
        // },
        // {
        //   filename: "444-UCH-3-IDA - 2020-06-11 16.16.37",
        //   description:
        //     "198268 - A2  -  Adenoma de colon",
        //   staining: "Hematoxilina-eosina"
        // }
      ]
      // base_URL: "http://ndp.microscopiavirtual.com/NDPServe.dll"
    };
  },
  computed: {
    footerProps() {
      return {
        "items-per-page-options": [5, 10, 15],
        "items-per-page-text": this.$t("report.add.footerText")
      }
    },
    headers() {
      return [
        {
          text: null,
          value: null,
          align: "start",
          width: "5%",
          sortable: false
        },
        {
          text: this.$t("report.add.fileName"),
          align: "start",
          sortable: "false",
          value: "filename"
        },
        {
          text: this.$t("report.add.description"),
          value: "description"
        },
        {
          text: this.$t("report.add.staining"),
          value: "staining"
        },
        {
          text: this.$t("report.add.pathology"),
          value: "organ"
        },
        {
          text: this.$t("report.add.prediagnostic"),
          value: "prediagnostic",
          align: " d-none"
        },
        {
          text: this.$t("report.add.serviceType"),
          value: "service"
        },
        {
          text: this.$t("report.add.priority"),
          value: "priority"
        },
        {
          text: this.$t("report.add.outputPriority"),
          value: "output",
          align: " d-none"
        },
        {
          text: this.$t("report.add.comment"),
          value: "comment",
          class: "col-2 text-truncate comment-cell"
        }
      ];
    },
    serviceTypes() {
      return [
        {
          id: 0,
          text: this.$t("report.serviceType.diagnostic_suggestion.title"),
          value: "service_1",
          color: "black",
          tooltip: this.$t("report.serviceType.diagnostic_suggestion.description")
        },
        {
          id: 1,
          text: this.$t("report.serviceType.quality_control.title"),
          value: "service_2",
          color: "grey",
          tooltip: this.$t("report.serviceType.quality_control.description")
        }
      ];
    },
    organTypes() {
      return [
        {
          id: 0,
          text: this.$t("report.organOptions.colon"),
          color: "red",
          value: "colon"
        },
        {
          id: 1,
          text: this.$t("report.organOptions.gastric"),
          color: "pink",
          value: "gastric"
        },
        {
          id: 2,
          text: this.$t("report.organOptions.melanoma"),
          color: "purple",
          value: "melanoma"
        }
      ];
    },
    amount() {
      return this.selected.length;
    },
    price() {
      // return this.amount * Math.ceil(Math.random() * 10);
      return this.amount * 10;
    },
    wallet() {
      return 1000;
    },
    final_balance() {
      return this.wallet - this.price;
    }
  },
  watch: {
    value(val) {
      if (val) {
        this.service = null;
        this.showList = false;
        this.selected = [];
      }
    }
  },
  methods: {
    calculatePriority() {
      this.selected.forEach((item, index) => {
        item.priority = index + 1;
      });
      this.selected.sort((a, b) => (a.priority > b.priority ? 1 : -1));
    },
    toggle(item) {
      if (this.selected.includes(item)) {
        const index = this.selected.indexOf(item);
        if (index > -1) this.selected.splice(index, 1);
        this.clearItem(item);
      } else {
        this.selected.push(item);
      }
      this.calculatePriority();
    },
    clear() {
      this.selected.forEach(item => this.clearItem(item));
      this.selected = [];
    },
    clearItem(item) {
      item.priority = 0;
      // item.organ = 0;
      // item.prediagnostic = 0;
      // item.service = 0;
      // item.output = 0;
      item.comment = null;
    },
    close(ref, value) {
      this.$refs[ref].save(value);
    },
    closePriority(ref, value) {
      const indexes = this.selected.reduce((array, item, index) => {
        if (item.priority === value) array.push(index);
        return array;
      }, []);
      const temp = this.selected[indexes[0]];
      this.selected[indexes[0]] = this.selected[indexes[1]];
      this.selected[indexes[1]] = temp;
      this.calculatePriority();
      this.$refs[ref].save(value);
    },
    saveComment(ref, comment) {
      if ((comment || "").length <= this.commentMaxLength)
        this.$refs[ref].save(comment);
      else this.$refs[ref].save(this.originalComment);
    },
    saveOrder(event) {
      const movedItem = this.selected.splice(event.oldIndex, 1)[0];
      this.selected.splice(event.newIndex, 0, movedItem);
      this.calculatePriority();
    },
    resetDialog() {
      this.clear()
      // this.reportBatch = { service: "", reportwsis: "" }
      this.$emit("input", false);
    },
    showWSIList() {
      this.showList = true;
    },
    async submit() {
      // this.$refs.form.validate();
      const wsis = this.selected.map(wsi => {
        return {
          filename: wsi.filename,
          biopsy_id: wsi.filename.split(" ")[0],
          plaque_id: wsi.description.split(" ")[0],
          service_type: this.serviceTypes[wsi.service].value,
          identifier: null,
          profile: null,
          priority: wsi.priority,
        };
      });
      console.log(wsis)
      this.reportBatch = { reportwsis: wsis };
      this.resetDialog();
      await this.$store.dispatch(CREATE_REPORTBATCH, this.reportBatch);
      this.$notify({
        type: "success",
        text: "Classifiction in queue"
      });
    }
  },
  directives: {
    sortableDataTable: {
      bind(el, binding, vnode) {
        const options = {
          animation: 150,
          onUpdate: function(event) {
            vnode.child.$emit("sorted", event);
          }
        };
        Sortable.create(el.getElementsByTagName("tbody")[0], options);
      }
    }
  }
};
</script>

<style scoped>
>>> #comment-textarea {
  max-height: 300px;
  overflow-y: auto;
}
>>> .comment-cell {
  overflow-y: clip;
}
>>> .theme--light.v-data-table tbody tr.v-data-table__selected {
  background: #00bcd4;
}
>>> .theme--light.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
  background: #b2ebf2;
}
>>> .v-dialog--fullscreen {
  border-radius: 0;
  margin: 0;
  /* height: 100%; */
  position: fixed;
  overflow-y: auto;
  top: 0;
  left: 0;
}
</style>
